/**
 * Sorts a refusal of declaring the account a master account, or of editing the master account's identity inside its
 * own books, into the `systemSettings.organization.masterAccountErrors.*` translation key suffix.
 */
import { CombinedGraphQLErrors } from '@apollo/client';
import { GraphQLErrorCode } from '@insio/validation';

export const MasterAccountDeclarationErrorReason = {
  /** The people sent with the declaration no longer match the account's members. */
  CLASSIFICATION_INVALID: 'classificationInvalid',
  /** The account already is a master account. */
  ALREADY_A_MASTER_ACCOUNT: 'alreadyAMasterAccount',
  /** The account is a master account's client, or would nest a master account under another one. */
  MASTER_ACCOUNT_DOES_NOT_NEST: 'masterAccountDoesNotNest',
  /** The members changed while the declaration ran. */
  DECLARE_CONFLICT: 'declareConflict',
  /** The caller is not an active administrator, or acts under impersonation. */
  FORBIDDEN: 'forbidden',
  /** The master account's identity was edited inside its own books. */
  OWN_BOOKS_IDENTITY_READ_ONLY: 'ownBooksIdentityReadOnly'
} as const;

export type MasterAccountDeclarationErrorReason =
  (typeof MasterAccountDeclarationErrorReason)[keyof typeof MasterAccountDeclarationErrorReason];

const REASON_BY_CODE: Readonly<Record<string, MasterAccountDeclarationErrorReason>> = {
  [GraphQLErrorCode.MASTER_ACCOUNT_MEMBER_CLASSIFICATION_INVALID]:
    MasterAccountDeclarationErrorReason.CLASSIFICATION_INVALID,
  [GraphQLErrorCode.ALREADY_A_MASTER_ACCOUNT]:
    MasterAccountDeclarationErrorReason.ALREADY_A_MASTER_ACCOUNT,
  [GraphQLErrorCode.MASTER_ACCOUNT_DOES_NOT_NEST]:
    MasterAccountDeclarationErrorReason.MASTER_ACCOUNT_DOES_NOT_NEST,
  [GraphQLErrorCode.MASTER_ACCOUNT_DECLARE_CONFLICT]:
    MasterAccountDeclarationErrorReason.DECLARE_CONFLICT,
  [GraphQLErrorCode.FORBIDDEN]: MasterAccountDeclarationErrorReason.FORBIDDEN,
  [GraphQLErrorCode.OWN_BOOKS_IDENTITY_READ_ONLY]:
    MasterAccountDeclarationErrorReason.OWN_BOOKS_IDENTITY_READ_ONLY
};

/** The reason behind `error`, or `undefined` when it is none of the refusals above. */
export function masterAccountDeclarationErrorReason(
  error: unknown
): MasterAccountDeclarationErrorReason | undefined {
  if (!CombinedGraphQLErrors.is(error)) return undefined;

  for (const gqlError of error.errors) {
    const code = gqlError.extensions?.['code'];
    if (typeof code === 'string' && Object.hasOwn(REASON_BY_CODE, code)) {
      return REASON_BY_CODE[code];
    }
  }

  return undefined;
}
